import { useCallback, useMemo } from 'react';
import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';
import { agentHasLabel, agentInGroup } from '@reflex/shared';
import { api } from '@/lib/api';
import { normalizeAgentListFilters, queryKeys } from '@/lib/query-keys';
import { useOrgKey } from '@/hooks/auth/useOrgKey';
import { TOP_PROGRESS } from '@/components/layout/top-progress-store';
import { useSocketMessage } from '../socket/useSocket';
import type {
  Agent,
  AgentConfig,
  AgentMark,
  AgentStatus,
  ArchiveAllAgentsRequest,
  PaginatedAgentsResponse,
  WsServerMessage,
} from '@/lib/types';

function associationsKey(agent: Agent): string {
  // Ids alone miss in-place refreshes (a re-registration merging fields into
  // an existing row's data payload), so fold the mutable payload in too.
  return (agent.associations ?? [])
    .map((a) => `${a.id}:${a.role}:${a.externalId}:${JSON.stringify(a.data ?? null)}`)
    .join(',');
}

function agentChanged(prev: Agent, next: Agent): boolean {
  return (
    prev.status !== next.status ||
    // A turn can end while status stays `running`, so a broadcast that only
    // moves the turn boundary must still land or the sidebar keeps spinning.
    prev.turnState !== next.turnState ||
    // Background-only updates do not move the turn boundary or lastActiveAt.
    // Keep both liveness and membership live in the detail/sidebar caches.
    prev.hasBackgroundWork !== next.hasBackgroundWork ||
    JSON.stringify(prev.backgroundTasks) !== JSON.stringify(next.backgroundTasks) ||
    prev.lastActiveAt !== next.lastActiveAt ||
    prev.name !== next.name ||
    prev.archived !== next.archived ||
    prev.pinned !== next.pinned ||
    prev.streamId !== next.streamId ||
    prev.devboxId !== next.devboxId ||
    prev.tunnelKey !== next.tunnelKey ||
    prev.prUrl !== next.prUrl ||
    // The mark drives the row's leading icon, and the server clears it on its
    // own (turn start, archive) — so a broadcast that only changes the mark
    // still has to land, or a marked row keeps its icon after the run moves on.
    prev.mark !== next.mark ||
    associationsKey(prev) !== associationsKey(next)
  );
}

/**
 * Merge a broadcast agent onto the copy already in cache, keeping the
 * per-caller fields the broadcast cannot carry.
 *
 * `viewerAccess` is computed per caller by the read routes and stripped
 * from the org-wide fan-out (see `shareable` in `ws-broadcast.service.ts`).
 * Taking the broadcast wholesale would drop it on the first live update and
 * silently un-gate a viewer's controls mid-session.
 */
function mergeBroadcast(prev: Agent | undefined, next: Agent): Agent {
  return prev?.viewerAccess !== undefined && next.viewerAccess === undefined
    ? { ...next, viewerAccess: prev.viewerAccess }
    : next;
}

type AgentsInfiniteData = InfiniteData<PaginatedAgentsResponse, string | null>;

function prependToFirstPage(old: AgentsInfiniteData, agent: Agent): AgentsInfiniteData {
  const firstPage = old.pages[0];
  return {
    ...old,
    pages: [
      { agents: [agent, ...firstPage.agents], nextCursor: firstPage.nextCursor },
      ...old.pages.slice(1),
    ],
  };
}

/**
 * Drop `agentIds` from every page of a cached list.
 */
function removeAgentsFromPages(
  old: AgentsInfiniteData,
  agentIds: ReadonlySet<string>,
): AgentsInfiniteData {
  return {
    ...old,
    pages: old.pages.map((page) => ({
      ...page,
      agents: page.agents.filter((a) => !agentIds.has(a.id)),
    })),
  };
}

/**
 * Converge every org-scoped cache that can carry an agent after deletion.
 */
function evictDeletedAgents(
  queryClient: QueryClient,
  orgKey: string,
  agentIds: ReadonlySet<string>,
): void {
  queryClient.setQueriesData<AgentsInfiniteData>({ queryKey: ['agents', orgKey] }, (old) =>
    old ? removeAgentsFromPages(old, agentIds) : undefined,
  );
  for (const prefix of ['pinned-agents', 'agent-search'] as const) {
    queryClient.setQueriesData<PaginatedAgentsResponse>({ queryKey: [prefix, orgKey] }, (old) =>
      old ? { ...old, agents: old.agents.filter((agent) => !agentIds.has(agent.id)) } : undefined,
    );
  }
  for (const agentId of agentIds) {
    queryClient.removeQueries({ queryKey: [...queryKeys.agent(orgKey, agentId)], exact: true });
  }
  void queryClient.invalidateQueries({ queryKey: [...queryKeys.agentGroups(orgKey)] });
  void queryClient.invalidateQueries({ queryKey: [...queryKeys.agentLabels(orgKey)] });
}

function evictDeletedAgent(queryClient: QueryClient, orgKey: string, agentId: string): void {
  evictDeletedAgents(queryClient, orgKey, new Set([agentId]));
}

/**
 * The fields an archive flips, as the server leaves them. Archiving clears the
 * triage mark alongside the flag (`setArchived`), so both travel together —
 * applying only `archived` locally would let the row settle once now and again
 * when the broadcast arrives with the mark gone.
 */
interface ArchiveState {
  archived: boolean;
  /** Omit to leave the row's mark alone — only archiving clears it. */
  mark?: AgentMark | null;
}

const ARCHIVED_STATE: ArchiveState = { archived: true, mark: null };
const UNARCHIVED_STATE: ArchiveState = { archived: false };

/**
 * Visit every cached agents list, with the server facets its key was fetched
 * with. Each `['agents', orgKey, ...]` entry is a different *filtered* list, so
 * anything that splices rows in or out has to answer "does this row belong in
 * this one?" — which only the key can say.
 */
function forEachAgentListQuery(
  queryClient: QueryClient,
  orgKey: string,
  visit: (queryKey: readonly unknown[], filters: AgentServerFilters | undefined) => void,
): void {
  for (const query of queryClient.getQueryCache().findAll({ queryKey: ['agents', orgKey] })) {
    visit(query.queryKey, query.queryKey[2] as AgentServerFilters | undefined);
  }
}

/**
 * Read the archive state each of `ids` currently holds, so a failed request can
 * put it back.
 *
 * Lists first: they are what the sidebar renders and what the socket keeps
 * patched. A detail entry (`['agent', org, id]`) is only kept live while a
 * `useAgent` for that exact id is mounted, so after navigating away it lingers
 * for `gcTime` receiving no updates — restoring its stale `mark` would flip the
 * row's leading icon to something the server no longer has.
 */
function readArchiveStates(
  queryClient: QueryClient,
  orgKey: string,
  ids: readonly string[],
): Map<string, ArchiveState> {
  const states = new Map<string, ArchiveState>();
  const remaining = new Set(ids);
  const record = (agent: Agent): void => {
    if (!remaining.delete(agent.id)) return;
    states.set(agent.id, { archived: agent.archived, mark: agent.mark ?? null });
  };

  forEachAgentListQuery(queryClient, orgKey, (queryKey) => {
    const data = queryClient.getQueryData<AgentsInfiniteData>(queryKey);
    for (const page of data?.pages ?? []) page.agents.forEach(record);
  });
  for (const prefix of ['pinned-agents', 'agent-search'] as const) {
    for (const [, data] of queryClient.getQueriesData<PaginatedAgentsResponse>({
      queryKey: [prefix, orgKey],
    })) {
      data?.agents.forEach(record);
    }
  }
  for (const id of [...remaining]) {
    const agent = queryClient.getQueryData<Agent>([...queryKeys.agent(orgKey, id)]);
    if (agent) record(agent);
  }
  return states;
}

/**
 * Write archive state straight into every cache that can carry a row, instead
 * of waiting for the request and the `agent:updated` broadcast behind it.
 *
 * Archiving used to be purely socket-driven, which left the row in the active
 * list for a full round-trip — and, worse, meant the page picked "the next run
 * to open" from a list that still held the run being archived, so the viewer
 * bounced back into it before the broadcast moved it out.
 *
 * A row that stops matching a filtered list's server facets is dropped from it
 * (archiving clears the mark, so a mark-filtered list no longer contains it),
 * exactly as the socket handler would. That drop is not restored by a rollback;
 * a refetch of that list brings the row back.
 */
function applyArchiveStates(
  queryClient: QueryClient,
  orgKey: string,
  states: ReadonlyMap<string, ArchiveState>,
): void {
  if (states.size === 0) return;
  const patch = (agent: Agent): Agent => {
    const state = states.get(agent.id);
    if (!state) return agent;
    const mark = state.mark === undefined ? (agent.mark ?? null) : state.mark;
    if (agent.archived === state.archived && (agent.mark ?? null) === mark) return agent;
    return { ...agent, archived: state.archived, mark };
  };

  forEachAgentListQuery(queryClient, orgKey, (queryKey, filters) => {
    queryClient.setQueryData<AgentsInfiniteData>(queryKey, (old) => {
      if (!old) return undefined;
      let changed = false;
      const pages = old.pages.map((page) => {
        let pageChanged = false;
        const agents = page.agents.flatMap((agent) => {
          const next = patch(agent);
          if (next === agent) return [agent];
          pageChanged = true;
          return matchesServerFilters(next, filters) ? [next] : [];
        });
        if (!pageChanged) return page;
        changed = true;
        return { ...page, agents };
      });
      return changed ? { ...old, pages } : old;
    });
  });

  for (const prefix of ['pinned-agents', 'agent-search'] as const) {
    queryClient.setQueriesData<PaginatedAgentsResponse>({ queryKey: [prefix, orgKey] }, (old) => {
      if (!old) return undefined;
      const agents = old.agents.map(patch);
      return agents.some((agent, i) => agent !== old.agents[i]) ? { ...old, agents } : old;
    });
  }

  for (const id of states.keys()) {
    queryClient.setQueryData<Agent>([...queryKeys.agent(orgKey, id)], (old) =>
      old ? patch(old) : old,
    );
  }
}

/** Flip `ids` to archived (or back) across every cache in one pass. */
function setArchivedInCaches(
  queryClient: QueryClient,
  orgKey: string,
  ids: readonly string[],
  state: ArchiveState,
): void {
  applyArchiveStates(queryClient, orgKey, new Map(ids.map((id) => [id, state])));
}

/**
 * Take the caches over for an archive flip: stop any list fetch already in
 * flight, snapshot the rows so a failure can put them back, then apply it.
 *
 * The cancel is load-bearing. React Query resolves a fetch by writing the
 * response into the cache — it does not compare it against what is already
 * there — and the agent lists refetch on window focus. Without this, tabbing
 * back and archiving inside the request window lets the in-flight page land
 * with `archived: false` on top of the flip, putting the row back in the active
 * list until the broadcast arrives: the exact flash this is here to remove.
 */
async function beginArchiveFlip(
  queryClient: QueryClient,
  orgKey: string,
  ids: readonly string[],
  state: ArchiveState,
): Promise<Map<string, ArchiveState>> {
  await queryClient.cancelQueries({ queryKey: ['agents', orgKey] });
  await queryClient.cancelQueries({ queryKey: ['pinned-agents', orgKey] });
  const previous = readArchiveStates(queryClient, orgKey, ids);
  setArchivedInCaches(queryClient, orgKey, ids, state);
  return previous;
}

/**
 * Put `ids` back after a failed request, then refetch the lists: a row the flip
 * dropped out of a filtered list (archiving clears the mark, so a mark-filtered
 * list stops containing it) is not something a per-id patch can restore — only
 * the server knows it belongs there again.
 */
function rollbackArchiveFlip(
  queryClient: QueryClient,
  orgKey: string,
  ids: readonly string[],
  previous: ReadonlyMap<string, ArchiveState> | undefined,
): void {
  restoreArchiveStates(queryClient, orgKey, ids, previous);
  void queryClient.invalidateQueries({ queryKey: ['agents', orgKey] });
}

/**
 * Undo an optimistic flip for `ids`, putting back the state
 * {@link readArchiveStates} captured before the request went out.
 */
function restoreArchiveStates(
  queryClient: QueryClient,
  orgKey: string,
  ids: readonly string[],
  previous: ReadonlyMap<string, ArchiveState> | undefined,
): void {
  if (!previous) return;
  const restore = new Map<string, ArchiveState>();
  for (const id of ids) {
    const state = previous.get(id);
    if (state) restore.set(id, state);
  }
  applyArchiveStates(queryClient, orgKey, restore);
}

/**
 * Show a just-started run in the cached lists it actually belongs to.
 *
 * Every `['agents', orgKey, ...]` entry is a different *filtered* list now, so
 * a blanket `setQueriesData` over that prefix would put a brand-new run —
 * which is in no group, carries no mark, and is `starting` — into the "runs in
 * the payments group" list too. Each entry's key carries the facets it was
 * fetched with, so walk the cache and seed only the ones the run matches; the
 * server would return exactly those on a refetch.
 */
function seedStartedAgent(queryClient: QueryClient, orgKey: string, agent: Agent): void {
  forEachAgentListQuery(queryClient, orgKey, (queryKey, filters) => {
    if (!matchesServerFilters(agent, filters)) return;
    queryClient.setQueryData<AgentsInfiniteData>(queryKey, (old) => {
      if (!old) return undefined;
      if (old.pages.some((p) => p.agents.some((a) => a.id === agent.id))) return old;
      return prependToFirstPage(old, agent);
    });
  });
}

// Flatten pages to one row per run. The WS handler can prepend a broadcast
// copy to page 0 before the page that actually holds the run is fetched,
// leaving the same id in two pages. The first occurrence wins (it's the copy
// the handler has been patching); viewerAccess is taken from the fetched copy
// because the org-wide broadcast strips that per-caller field.
function flattenPages(data: { pages: PaginatedAgentsResponse[] } | undefined): Agent[] {
  const byId = new Map<string, Agent>();
  for (const page of data?.pages ?? []) {
    for (const fetched of page?.agents ?? []) {
      const wsBroadcast = byId.get(fetched.id);
      // wsBroadcast wins as `next`; fetched supplies viewerAccess as `prev` if
      // the broadcast stripped it.
      byId.set(fetched.id, wsBroadcast ? mergeBroadcast(fetched, wsBroadcast) : fetched);
    }
  }
  return [...byId.values()];
}

export function useAgent(agentId: string | undefined) {
  const queryClient = useQueryClient();
  const orgKey = useOrgKey();

  useSocketMessage(
    useCallback(
      (msg: WsServerMessage) => {
        if (!agentId) return;
        if (msg.type === 'agent:updated' && msg.agent.id === agentId) {
          // Defence-in-depth: the server already filters by org, but a stale
          // broadcast can land between an org switch and the WS reconnect.
          if (msg.agent.organizationId !== orgKey) return;
          queryClient.setQueryData<Agent>([...queryKeys.agent(orgKey, agentId)], (old) =>
            old && !agentChanged(old, msg.agent) ? old : mergeBroadcast(old, msg.agent),
          );
        } else if (msg.type === 'agent:deleted' && msg.agentId === agentId) {
          evictDeletedAgent(queryClient, orgKey, agentId);
        }
      },
      [queryClient, agentId, orgKey],
    ),
  );

  return useQuery({
    // The agent record is the page the user navigated to — its initial load
    // drives the global top progress bar.
    meta: { [TOP_PROGRESS]: true },
    queryKey: [...queryKeys.agent(orgKey, agentId!)],
    queryFn: () => api.getAgent(agentId!),
    enabled: !!agentId,
  });
}

/**
 * Whether a broadcast row belongs in a list narrowed by `filters`.
 *
 * The server applies these facets in SQL; a WS broadcast bypasses that query
 * entirely, so without the same check here a run that does not match would be
 * spliced into a filtered cache — a list that says "payments group" showing a
 * run that is in no group. Only the facets the server enforces are checked:
 * the client-side ones are re-applied by `filterAgents` on render.
 *
 * Group and label membership rides on the agent's associations, which the
 * broadcast carries, so both are answerable from the row itself.
 */
function matchesServerFilters(agent: Agent, filters: AgentServerFilters | undefined): boolean {
  if (!filters) return true;
  if (filters.userId && agent.userId !== filters.userId) return false;
  if (filters.statuses && !filters.statuses.includes(agent.status)) return false;
  if (filters.mark && (filters.mark === 'any' ? !agent.mark : agent.mark !== filters.mark)) {
    return false;
  }
  if (filters.groupId && !agentInGroup(agent, filters.groupId)) return false;
  if (filters.labelId && !agentHasLabel(agent, filters.labelId)) return false;
  // Null/undefined is "both", so compare only when the facet is set.
  if (filters.archived != null && agent.archived !== filters.archived) return false;
  return true;
}

/**
 * The agent-list facets `useAgents` hands to the server. See
 * {@link AgentListQueryFilters} for why they belong there rather than in a
 * client-side pass over the loaded pages.
 */
export interface AgentServerFilters {
  userId?: string | null;
  statuses?: readonly AgentStatus[] | null;
  mark?: AgentMark | 'any' | null;
  groupId?: string | null;
  labelId?: string | null;
  /** `true` for the archived list; unset (the default) for both. */
  archived?: boolean | null;
}

export function useAgents(filters?: AgentServerFilters) {
  const queryClient = useQueryClient();
  const orgKey = useOrgKey();
  // Callers rebuild their filter object every render, so identity is useless
  // as a dependency. Normalising to the canonical form the query key uses
  // (nulls dropped, keys sorted) gives a hash that is stable for as long as
  // the filters *mean* the same thing, and re-parsing it yields one object
  // identity per distinct meaning — which is what the socket callback and the
  // query below can safely depend on.
  const filterHash = JSON.stringify(normalizeAgentListFilters(filters) ?? null);
  const activeFilters = useMemo(
    () => (JSON.parse(filterHash) as AgentServerFilters | null) ?? undefined,
    [filterHash],
  );
  const queryKey = useMemo(
    () => [...queryKeys.agents(orgKey, activeFilters)],
    [orgKey, activeFilters],
  );

  useSocketMessage(
    useCallback(
      (msg: WsServerMessage) => {
        const key = queryKey;
        if (msg.type === 'agent:created') {
          // Defence-in-depth: the server's `broadcastAgentCreated` already
          // filters by org, but a stale broadcast can still arrive in the
          // window between switching orgs and the WS reconnecting with the
          // new `organizationId` param. Without this guard, a new agent in
          // another org would briefly appear in the currently-viewed org's
          // list cache.
          if (msg.agent.organizationId !== orgKey) return;
          // Facet filters only — visibility is NOT this guard's job. The
          // server scopes the broadcast audience to the rows the viewer may
          // see (`broadcastAgentScoped`); with the facets cleared this is a
          // no-op, and every row that arrives is one the list route would
          // also have returned.
          if (!matchesServerFilters(msg.agent, activeFilters)) return;
          queryClient.setQueryData<AgentsInfiniteData>(key, (old) => {
            if (!old) return undefined;
            const allAgents = old.pages.flatMap((p) => p.agents);
            if (allAgents.some((a) => a.id === msg.agent.id)) return old;
            return prependToFirstPage(old, msg.agent);
          });
        } else if (msg.type === 'agent:updated') {
          if (msg.agent.organizationId !== orgKey) return;
          // An update can move a run *out* of a filtered list — marking it
          // done while the list shows only running runs, filing it out of the
          // group being viewed. Dropping it here keeps the cache honest;
          // without it a filtered list slowly fills with rows that no longer
          // match and that a refetch would not return.
          if (!matchesServerFilters(msg.agent, activeFilters)) {
            queryClient.setQueryData<AgentsInfiniteData>(key, (old) =>
              old ? removeAgentsFromPages(old, new Set([msg.agent.id])) : undefined,
            );
            return;
          }
          queryClient.setQueryData<AgentsInfiniteData>(key, (old) => {
            if (!old) return undefined;
            let found = false;
            let changed = false;
            const pages = old.pages.map((page) => {
              const idx = page.agents.findIndex((a) => a.id === msg.agent.id);
              if (idx === -1) return page;
              found = true;
              if (!agentChanged(page.agents[idx], msg.agent)) return page;
              changed = true;
              const updated = [...page.agents];
              updated[idx] = mergeBroadcast(page.agents[idx], msg.agent);
              return { ...page, agents: updated };
            });
            if (!found) return prependToFirstPage({ ...old, pages }, msg.agent);
            if (!changed) return old;
            return { ...old, pages };
          });
        } else if (msg.type === 'agent:deleted') {
          evictDeletedAgent(queryClient, orgKey, msg.agentId);
        }
      },
      [queryClient, activeFilters, orgKey, queryKey],
    ),
  );

  const result = useInfiniteQuery({
    // The run list is the content of the agents page — its initial load
    // drives the global top progress bar.
    meta: { [TOP_PROGRESS]: true },
    queryKey,
    queryFn: ({ pageParam }) =>
      api.listAgents({
        userId: activeFilters?.userId ?? undefined,
        statuses: activeFilters?.statuses ?? undefined,
        mark: activeFilters?.mark ?? undefined,
        groupId: activeFilters?.groupId ?? undefined,
        labelId: activeFilters?.labelId ?? undefined,
        archived: activeFilters?.archived ?? undefined,
        cursor: pageParam ?? undefined,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });

  const agents = useMemo(() => flattenPages(result.data), [result.data]);

  return {
    ...result,
    data: agents,
    fetchNextPage: result.fetchNextPage,
    hasNextPage: result.hasNextPage,
    isFetchingNextPage: result.isFetchingNextPage,
    /** Pages held for these filters — the budget `useAgentListCompletion` spends. */
    pagesLoaded: result.data?.pages.length ?? 0,
  };
}

/**
 * Pinned agents, fetched independently of the paginated {@link useAgents}
 * list. The sidebar's Pinned section was previously derived by filtering the
 * pages the infinite-scroll list happened to have loaded, so a pinned run old
 * enough to sit past those pages silently vanished from the section until the
 * user scrolled far enough to load it. This asks the server for *all* pinned
 * rows (`GET /agents?pinned=true`) in one non-paginated read, so pinning is
 * durable regardless of scroll position.
 *
 * The WS seeding mirrors {@link useAgents} but keys off `pinned`: an
 * `agent:updated` that pins a run inserts it, one that unpins removes it, so
 * the section reacts live to pin toggles without a refetch.
 */
export function usePinnedAgents(filterUserId?: string | null) {
  const queryClient = useQueryClient();
  const orgKey = useOrgKey();
  const queryKey = [...queryKeys.pinnedAgents(orgKey, filterUserId)];

  useSocketMessage(
    useCallback(
      (msg: WsServerMessage) => {
        if (msg.type === 'agent:deleted') {
          evictDeletedAgent(queryClient, orgKey, msg.agentId);
          return;
        }
        if (msg.type !== 'agent:created' && msg.type !== 'agent:updated') return;
        // Defence-in-depth org guard, matching useAgents: a stale broadcast
        // can land between an org switch and the WS reconnect.
        if (msg.agent.organizationId !== orgKey) return;
        if (filterUserId && msg.agent.userId !== filterUserId) return;
        const key = [...queryKeys.pinnedAgents(orgKey, filterUserId)];
        queryClient.setQueryData<PaginatedAgentsResponse>(key, (old) => {
          if (!old) return undefined;
          const idx = old.agents.findIndex((a) => a.id === msg.agent.id);
          if (msg.agent.pinned) {
            if (idx === -1) return { ...old, agents: [msg.agent, ...old.agents] };
            if (!agentChanged(old.agents[idx], msg.agent)) return old;
            const updated = [...old.agents];
            updated[idx] = msg.agent;
            return { ...old, agents: updated };
          }
          // No longer pinned — drop it from the section.
          if (idx === -1) return old;
          return { ...old, agents: old.agents.filter((a) => a.id !== msg.agent.id) };
        });
      },
      [queryClient, filterUserId, orgKey],
    ),
  );

  const result = useQuery({
    queryKey,
    // The 200 cap matches the route's `limit` ceiling. Pinned sets are small
    // by nature (a handful of runs a user keeps at hand), so a single page
    // comfortably covers them without pagination.
    queryFn: () => api.listAgents({ userId: filterUserId ?? undefined, pinned: true, limit: 200 }),
  });

  const agents = useMemo(() => result.data?.agents ?? [], [result.data]);

  return { ...result, data: agents };
}

/**
 * Server-side agent-run search, used by the `@`-mention run picker and by the
 * sidebar search box. Unlike {@link useAgents} — which loads a client-held
 * page and is filtered locally — this asks the API to match `search` against
 * every run's name and prompt in the org, so a run that is not on the loaded
 * pages (or is only findable by its prompt text) still surfaces. An empty
 * query returns the recent page.
 *
 * `userId` scopes the search the same way the list is scoped, so a search from
 * a "only me" list cannot surface someone else's runs.
 *
 * Kept as a plain read (no WS cache seeding): the results are transient, so
 * the live-update machinery `useAgents` carries would be dead weight here.
 */
export function useAgentSearch(
  search: string,
  limit = 10,
  options?: { enabled?: boolean; userId?: string | null },
) {
  const orgKey = useOrgKey();
  const trimmed = search.trim();
  const userId = options?.userId ?? null;

  const result = useQuery({
    queryKey: [...queryKeys.agentSearch(orgKey, trimmed, userId)],
    queryFn: () =>
      api.listAgents({ search: trimmed || undefined, limit, userId: userId ?? undefined }),
    // Keep the prior results on screen while the next keystroke's query
    // resolves, so the list refines instead of flashing empty.
    placeholderData: keepPreviousData,
    enabled: options?.enabled ?? true,
  });

  const agents = useMemo(() => result.data?.agents ?? [], [result.data]);

  return { ...result, data: agents };
}

export function useStartAgent() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useMutation({
    mutationFn: (config: AgentConfig) => api.startAgent(config),
    onSuccess: (agent) => {
      // Surface any non-fatal launch adjustments (e.g. a requested branch that
      // no longer existed, so the run fell back to the repo's default branch)
      // as a toast. The launch still succeeded, so this is a warning, not an
      // error. Fires here so both launch surfaces (form and dialog) get it.
      for (const warning of agent.launchWarnings ?? []) {
        toast.warning('Launched with a change', { description: warning });
      }
      qc.setQueryData<Agent>([...queryKeys.agent(orgKey, agent.id)], agent);
      seedStartedAgent(qc, orgKey, agent);
    },
  });
}

export function useInterruptAgent() {
  return useMutation({
    mutationFn: (id: string) => api.interruptAgent(id),
  });
}

export function useStopAgent() {
  return useMutation({
    mutationFn: (id: string) => api.stopAgent(id),
  });
}

export function useCompleteAgent() {
  return useMutation({
    mutationFn: (id: string) => api.completeAgent(id),
  });
}

/**
 * Hard-delete (kill) a single agent.
 *
 * The toasts live here rather than at the call site because the only caller is
 * the confirm dialog, which unmounts the moment the user confirms — React Query
 * skips `mutate`-level callbacks once an observer has no listeners, so a toast
 * passed to `mutate` would silently never fire. Mutation-level callbacks run
 * regardless.
 */
export function useKillAgent() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useMutation({
    mutationFn: (id: string) => api.killAgent(id),
    onSuccess: (_data, agentId) => {
      evictDeletedAgent(qc, orgKey, agentId);
      toast.success('Agent deleted');
    },
    onError: (err: Error) => toast.error('Failed to delete', { description: err.message }),
  });
}

/**
 * Bulk hard-delete (kill) for an explicit multi-selection in the sidebar.
 * There is no id-based batch endpoint, so this fans the per-agent
 * `DELETE /agents/:id` out with {@link Promise.allSettled} — the selection is a
 * bounded, user-picked set, so a handful of parallel requests is fine and we
 * inherit `killAgent`'s exact semantics. Successfully-killed ids are evicted
 * from every cached agents list (mirroring {@link useKillAgent}); a partial
 * failure is surfaced in a single toast rather than aborting the whole batch.
 */
export function useBulkKillAgents() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const results = await Promise.allSettled(ids.map((id) => api.killAgent(id)));
      const succeeded = ids.filter((_, i) => results[i].status === 'fulfilled');
      return { succeeded, failed: ids.length - succeeded.length };
    },
    onSuccess: ({ succeeded, failed }) => {
      if (succeeded.length) {
        evictDeletedAgents(qc, orgKey, new Set(succeeded));
        toast.success(`Deleted ${succeeded.length} agent${succeeded.length === 1 ? '' : 's'}`);
      }
      if (failed > 0) {
        toast.error(`Failed to delete ${failed} agent${failed === 1 ? '' : 's'}`);
      }
    },
  });
}

/**
 * Bulk archive for an explicit multi-selection. Archiving is non-destructive:
 * it only flips the `archived` flag (`POST /agents/:id/archive`) and leaves the
 * run and its devbox untouched, so — unlike the single-row "kill and archive"
 * path — running agents are not stopped. Like {@link useArchiveAllAgents}, the
 * archived rows flip in the list caches via the per-agent `agent:updated`
 * broadcasts the server emits, so no local cache surgery is needed here.
 */
export function useBulkArchiveAgents() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const results = await Promise.allSettled(ids.map((id) => api.archiveAgent(id)));
      // Undo only offers back the runs that actually archived, so a partial
      // failure cannot unarchive something the user never archived.
      const archivedIds = ids.filter((_id, i) => results[i].status === 'fulfilled');
      const failedIds = ids.filter((_id, i) => results[i].status === 'rejected');
      return { archivedIds, failedIds };
    },
    onMutate: (ids) =>
      beginArchiveFlip(qc, orgKey, ids, ARCHIVED_STATE).then((p) => ({
        previous: p,
      })),
    onSuccess: ({ archivedIds, failedIds }, _ids, context) => {
      // Only the runs that failed go back to where they were; the rest keep
      // the archived state `onMutate` already put on screen.
      if (failedIds.length > 0) {
        rollbackArchiveFlip(qc, orgKey, failedIds, context?.previous);
        toast.error(
          `Failed to archive ${failedIds.length} agent${failedIds.length === 1 ? '' : 's'}`,
        );
      }
      toastAgentsArchived(qc, orgKey, archivedIds);
    },
    onError: (_err, ids, context) => rollbackArchiveFlip(qc, orgKey, ids, context?.previous),
  });
}

/**
 * Bulk interrupt for an explicit multi-selection. Interrupting is
 * non-destructive: it cancels each agent's in-flight turn
 * (`POST /agents/:id/interrupt`) but leaves the run and its devbox up, so the
 * agents stay in the list (this is deliberately *not* equivalent to killing or
 * stopping them). Callers pass only the ids that are actually mid-turn; the
 * interrupted rows settle via the per-agent broadcasts the server emits, so no
 * local cache surgery is needed here.
 */
export function useBulkInterruptAgents() {
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const results = await Promise.allSettled(ids.map((id) => api.interruptAgent(id)));
      const failed = results.filter((r) => r.status === 'rejected').length;
      return { interrupted: ids.length - failed, failed };
    },
    onSuccess: ({ interrupted, failed }) => {
      if (interrupted > 0) {
        toast.success(`Stopped ${interrupted} agent${interrupted === 1 ? '' : 's'}`);
      }
      if (failed > 0) {
        toast.error(`Failed to stop ${failed} agent${failed === 1 ? '' : 's'}`);
      }
    },
  });
}

export function useUpdateAgent() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => api.updateAgent(id, { name }),
    onSuccess: (agent) => {
      // Server also broadcasts `agent:updated` over WS, but seeding the
      // cache here keeps the optimistic-feeling update instant for the
      // caller and avoids a flash when the WS message arrives a tick later.
      qc.setQueryData<Agent>([...queryKeys.agent(orgKey, agent.id)], agent);
      qc.setQueriesData<AgentsInfiniteData>({ queryKey: ['agents', orgKey] }, (old) => {
        if (!old) return undefined;
        return {
          ...old,
          pages: old.pages.map((page) => {
            const idx = page.agents.findIndex((a) => a.id === agent.id);
            if (idx === -1) return page;
            const updated = [...page.agents];
            updated[idx] = agent;
            return { ...page, agents: updated };
          }),
        };
      });
    },
  });
}

/**
 * Switch the model-provider subscription a running agent authenticates with.
 * The server applies the swap on the agent's next turn and broadcasts
 * `agent:updated`; we also seed the cache with the returned agent so the
 * detail panel reflects the new `modelProviderSecretId` without waiting for
 * the WS round-trip.
 */
export function useSwitchAgentSubscription() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useMutation({
    mutationFn: ({ id, providerSecretId }: { id: string; providerSecretId: string }) =>
      api.switchAgentSubscription(id, providerSecretId),
    onSuccess: (agent) => {
      qc.setQueryData<Agent>([...queryKeys.agent(orgKey, agent.id)], agent);
      qc.setQueriesData<AgentsInfiniteData>({ queryKey: ['agents', orgKey] }, (old) => {
        if (!old) return undefined;
        return {
          ...old,
          pages: old.pages.map((page) => {
            const idx = page.agents.findIndex((a) => a.id === agent.id);
            if (idx === -1) return page;
            const updated = [...page.agents];
            updated[idx] = agent;
            return { ...page, agents: updated };
          }),
        };
      });
    },
  });
}

/**
 * Switch a running agent's model. Seeds the cache with the returned agent so
 * the composer chip does not blink back to the old model before the WS
 * broadcast lands.
 */
export function useUpdateAgentRuntimeSettings() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useMutation({
    mutationFn: ({ id, model }: { id: string; model: string }) =>
      api.updateAgentRuntimeSettings(id, { model }),
    onSuccess: (agent) => {
      qc.setQueryData<Agent>([...queryKeys.agent(orgKey, agent.id)], agent);
      qc.setQueriesData<AgentsInfiniteData>({ queryKey: ['agents', orgKey] }, (old) => {
        if (!old) return undefined;
        return {
          ...old,
          pages: old.pages.map((page) => {
            const idx = page.agents.findIndex((a) => a.id === agent.id);
            if (idx === -1) return page;
            const updated = [...page.agents];
            updated[idx] = agent;
            return { ...page, agents: updated };
          }),
        };
      });
    },
  });
}

/**
 * Attach MCP servers to a running agent's devbox. The server registers the
 * servers on the box immediately and broadcasts `agent:updated` with the
 * grown `mcp-servers` attachment; we also seed the cache with the returned
 * agent so the composer's "already attached" subtraction and the detail
 * panel update without waiting for the WS round-trip.
 */
export function useAttachAgentMcpServers() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useMutation({
    mutationFn: ({ id, mcpServerIds }: { id: string; mcpServerIds: string[] }) =>
      api.attachAgentMcpServers(id, mcpServerIds),
    onSuccess: ({ agent }) => {
      qc.setQueryData<Agent>([...queryKeys.agent(orgKey, agent.id)], agent);
      qc.setQueriesData<AgentsInfiniteData>({ queryKey: ['agents', orgKey] }, (old) => {
        if (!old) return undefined;
        return {
          ...old,
          pages: old.pages.map((page) => {
            const idx = page.agents.findIndex((a) => a.id === agent.id);
            if (idx === -1) return page;
            const updated = [...page.agents];
            updated[idx] = agent;
            return { ...page, agents: updated };
          }),
        };
      });
    },
  });
}

/**
 * Fan out unarchive requests and report the outcome via toasts. Used by the
 * archived toast's undo action (both single and bulk). The rows flip back via
 * the `agent:updated` broadcasts, so there is no cache surgery here.
 */
async function unarchiveAgents(
  queryClient: QueryClient,
  orgKey: string,
  ids: string[],
): Promise<void> {
  // The rows come back the moment the undo is clicked, for the same reason the
  // archive flips on click: waiting for the round-trip reads as a dead button.
  setArchivedInCaches(queryClient, orgKey, ids, UNARCHIVED_STATE);
  const results = await Promise.allSettled(ids.map((id) => api.unarchiveAgent(id)));
  const rejected = results.filter((r) => r.status === 'rejected');
  const restored = ids.length - rejected.length;
  if (rejected.length > 0) {
    const failedIds = ids.filter((_id, i) => results[i].status === 'rejected');
    setArchivedInCaches(queryClient, orgKey, failedIds, ARCHIVED_STATE);
  }
  if (restored > 0) {
    toast.success(restored === 1 ? 'Agent unarchived' : `Unarchived ${restored} agents`);
  }
  if (rejected.length === 1) {
    // A single failure has one reason worth reading — a 403 on a view-only
    // grant reads nothing like a network blip — so pass it through.
    toast.error('Failed to unarchive', { description: asError(rejected[0].reason).message });
  } else if (rejected.length > 1) {
    toast.error(`Failed to unarchive ${rejected.length} agents`);
  }
}

function asError(reason: unknown): Error {
  return reason instanceof Error ? reason : new Error(String(reason));
}

/** Success toast for an archive, with the Unarchive affordance attached. */
function toastAgentsArchived(queryClient: QueryClient, orgKey: string, ids: string[]): void {
  if (ids.length === 0) return;
  let fired = false;
  toast.success(ids.length === 1 ? 'Agent archived' : `Archived ${ids.length} agents`, {
    action: {
      label: 'Unarchive',
      onClick: () => {
        if (fired) return;
        fired = true;
        void unarchiveAgents(queryClient, orgKey, ids);
      },
    },
  });
}

/**
 * Archive a single run. The success toast (and its Unarchive action) lives
 * here rather than at the call sites so every per-run archive entry point —
 * the sidebar row, the inbox, the state picker, the confirm dialog — confirms
 * the same way and offers the same way back. The bucket-level
 * {@link useArchiveAllAgents} is the exception: the server picks its set, so
 * there are no ids to offer back.
 */
export function useArchiveAgent() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useMutation({
    mutationFn: (id: string) => api.archiveAgent(id),
    onMutate: (id) =>
      beginArchiveFlip(qc, orgKey, [id], ARCHIVED_STATE).then((p) => ({
        previous: p,
      })),
    onSuccess: (_data, id) => toastAgentsArchived(qc, orgKey, [id]),
    onError: (err: Error, id, context) => {
      rollbackArchiveFlip(qc, orgKey, [id], context?.previous);
      toast.error('Failed to archive', { description: err.message });
    },
  });
}

/**
 * Bulk archive. Unlike looping {@link useArchiveAgent} on the client — which
 * only reaches the runs the sidebar has scrolled into memory — this issues a
 * single request the server fans out over every matching run. The archived
 * rows flip in the list caches live via the per-agent `agent:updated`
 * broadcasts the server emits as it archives, so no local cache surgery is
 * needed here.
 */
export function useArchiveAllAgents() {
  return useMutation({
    mutationFn: (body: ArchiveAllAgentsRequest = {}) => api.archiveAllAgents(body),
  });
}

/** Restore an archived run. */
export function useUnarchiveAgent() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useMutation({
    mutationFn: (id: string) => api.unarchiveAgent(id),
    onMutate: (id) =>
      beginArchiveFlip(qc, orgKey, [id], UNARCHIVED_STATE).then((p) => ({
        previous: p,
      })),
    onSuccess: () => toast.success('Agent unarchived'),
    onError: (err: Error, id, context) => {
      rollbackArchiveFlip(qc, orgKey, [id], context?.previous);
      toast.error('Failed to unarchive', { description: err.message });
    },
  });
}

export function usePinAgent() {
  return useMutation({
    mutationFn: (id: string) => api.pinAgent(id),
  });
}

export function useUnpinAgent() {
  return useMutation({
    mutationFn: (id: string) => api.unpinAgent(id),
  });
}

/**
 * Apply or clear an agent's triage mark. No cache surgery: the server
 * broadcasts the updated row over `agent:updated`, which is also the only way
 * the client learns about the marks the server clears by itself (turn start,
 * archive) — so leaving both directions to the socket keeps them consistent.
 */
export function useSetAgentMark() {
  return useMutation({
    mutationFn: ({ id, mark }: { id: string; mark: AgentMark | null }) =>
      api.setAgentMark(id, mark),
  });
}
